import { useLayoutEffect } from 'react';

import { setTouchTargetRotation } from '../../api/modules/hostMessaging/inputEvents';

import { computeTouchRotationDeg } from './touchRotation';

/**
 * Keeps the touch input module in sync with the video's active CSS rotation so
 * touch coordinates are un-rotated before normalization. Uses the same resolved
 * `forceRotateDeg` (90 or -90) that drives the `--force-rotate-angle` CSS var;
 * 0 when not force-rotated. Without this, fullscreen touches all map to the
 * bottom-right corner because getBoundingClientRect returns the rotated AABB.
 *
 * Uses `useLayoutEffect` (not `useEffect`) so the rotation is pushed
 * synchronously in the same commit that applies the CSS rotation, before the
 * browser paints. A passive `useEffect` runs after paint, leaving a one-frame
 * window where the DOM is already rotated but touch input still maps with the
 * previous rotation.
 *
 * Resets the rotation to 0 on unmount so a torn-down player never leaves the
 * shared input module in a rotated state.
 */
export const useTouchTargetRotationSync = (isVisuallyRotated: boolean, forceRotateDeg: number): void => {
  useLayoutEffect(() => {
    setTouchTargetRotation(computeTouchRotationDeg(isVisuallyRotated, forceRotateDeg));
    return () => setTouchTargetRotation(0);
  }, [isVisuallyRotated, forceRotateDeg]);
};
